import { useMutation } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import { downloadFile } from 'utils/AppHelper'
import APIService from 'api/APIService'
import { JobStatus, ExportEntityParams } from 'types/entity'
import Button from 'core/components/buttons/Button'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'
import { Dayjs } from 'utils/dayjs'

const useMemberExport = (onSuccess?: VoidFunction, allowDownload = true) => {
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const jobID = settings.pollingJobs.memberExportJobId

  const {
    mutate: exportData,
    status: exportDataStatus,
    isPending: exportingData
  } = useMutation({
    mutationFn: ({
      dates,
      context_user_email,
      entity,
      file_type
    }: Partial<ExportEntityParams> & {
      dates: [Dayjs, Dayjs]
    }) => {
      const [start_date, end_date] = dates
      return APIService.exports.exportData({
        start_day: start_date.format('DD'),
        start_month: start_date.format('MM'),
        start_year: start_date.format('YYYY'),
        end_day: end_date.format('DD'),
        end_month: end_date.format('MM'),
        end_year: end_date.format('YYYY'),
        context_user_email,
        entity,
        file_type
      })
    },
    onSuccess: (data) => {
      set('pollingJobs', {
        ...settings.pollingJobs,
        memberExportJobId: data.job_token_id
      })
      onSuccess?.()
    },
    onError: () => {
      notification.error('An error has occurred trying to download this file')
    }
  })

  const loadingNotificationId = `member-export-${jobID}`

  useQueryCallbacks({
    queryKey: ['member-export-polling', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      const errors = response?.error_list ?? []
      const hasErrors = errors.length > 0
      if (response.status === 'finished' && !hasErrors) {
        const { data } = response
        const downloadBoth = data.type === 'transactions & trips'
        if (downloadBoth && allowDownload) {
          notification.success({
            title: 'Data exports are ready for download',
            subtitle: 'Click below to access the file',
            duration: Infinity,
            className: '!max-w-[26rem] w-[26rem]',
            footer: () => (
              <div className="flex justify-end gap-5">
                <Button
                  variant="link"
                  className="text-primary"
                  onClick={() => downloadFile(data.trips_file_url as string)}
                >
                  Download Trips
                </Button>
                <Button
                  variant="link"
                  className="text-primary"
                  onClick={() =>
                    downloadFile(data.transactions_file_url as string)
                  }
                >
                  Download Expenses
                </Button>
              </div>
            )
          })
        } else if (allowDownload) {
          downloadFile(data.file_url as string)
          notification.success({
            title: `You have downloaded ${data.type} file`,
            id: jobID
          })
        }

        set('pollingJobs', {
          ...settings.pollingJobs,
          memberExportJobId: ''
        })

        notification.dismiss(loadingNotificationId)
      }

      if (
        (response.status === 'in_progress' || response.status === 'enqueued') &&
        !hasErrors
      ) {
        notification.loading({
          id: loadingNotificationId,
          position: 'bottom-left',
          title: 'Exporting your members...',
          subtitle: 'Downloading member list, this may take a few minutes.',
          duration: Infinity
        })
      }

      if (
        response.status === 'failed' ||
        (response.status === 'finished' && hasErrors)
      ) {
        notification.dismiss(loadingNotificationId)
        notification.error({
          title: 'There were some errors with your file',
          subtitle: (
            <div>
              {typeof response.data?.message === 'string' &&
                response.data.message?.split(';').map((error) => {
                  return <span key={error}>{error.replace(';', '')}</span>
                })}
            </div>
          ),
          id: jobID,
          footer: () => {
            return (
              <Button
                className="text-primary"
                variant="link"
                onClick={() => {
                  if (response.data.file_url && allowDownload)
                    downloadFile(response.data.file_url as string)
                }}
              >
                Download
              </Button>
            )
          }
        })
        set('pollingJobs', {
          ...settings.pollingJobs,
          memberExportJobId: ''
        })
      }
    },
    onError: () => {
      notification.dismiss(loadingNotificationId)
      notification.error({
        title: 'Could not export this member',
        id: jobID
      })
      set('pollingJobs', {
        ...settings.pollingJobs,
        memberExportJobId: ''
      })
    }
  })

  return {
    exportData,
    exportDataStatus,
    exportingData
  }
}

export default useMemberExport
